import settings from '@/config/settings';

import type {
  IRequestError,
  IRequestErrorBody,
  IRequestSuccess,
} from '@/assets/core/assets/api/types';

import {keycloakAPI} from '@/assets/keycloak';

import {EApiRequestMethod} from './enums';

import antMessage from '@/assets/ant/message';

export const getDetailsFromRequestError = (error: IRequestError | unknown) => {
  const detail = (error as IRequestError).isUserError
    ? ((error as IRequestError).body as IRequestErrorBody).detail
    : undefined;

  if (Array.isArray(detail)) {
    return detail
      .map((item) => `${item.msg} (${item.loc.join(', ')})`)
      .join('\n');
  } else if (typeof detail === 'object') {
    return Object.values(detail as Record<string, string>).join('\n');
  }

  return detail;
};

/**
 * Метод отправки нотификации об ошибке
 * @param {String} url - Адрес запроса
 * @param {String} method - Метод запроса
 * @param {Object} body - Тело запроса
 * @param {Object} headers - Заголовки запроса
 * @param {AbortSignal} signal - Сигнал отмены запроса
 * @return {Promise}
 * */
async function doRequest(
  url: URL | string,
  method: EApiRequestMethod = EApiRequestMethod.GET,
  body: unknown = null,
  headers: Record<string, string> = {},
  signal?: AbortSignal,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<any> {
  // fix object type
  if (typeof url === 'string') url = new URL(url);

  // fix headers
  if (headers['Content-Type'] == null)
    headers['Content-Type'] = 'application/json';

  if (!settings.APP.TEST) {
    if (keycloakAPI.getKeycloak.value.isTokenExpired()) {
      await keycloakAPI.getKeycloak.value.updateToken(-1);
    }
    if (keycloakAPI.getToken.value != null) {
      headers['Authorization'] = 'Bearer ' + keycloakAPI.getToken.value;
    }
  }

  const initConfig: RequestInit = {
    method: method,
    credentials: 'include',
    headers: headers,
  };

  if (body != null) initConfig['body'] = JSON.stringify(body);
  if (signal != null) initConfig['signal'] = signal;

  return await fetch(url, initConfig)
    .then((response: Response) => {
      if (
        response.headers.get('Content-Length') === '0' ||
        response.status === 204
      ) {
        if (response.ok) return {successful: true} as IRequestSuccess;
        else
          throw {isUserError: true, url: url.toString(), response, error: ''};
      }

      return response
        .json()
        .then((data) => {
          if (response.ok) return data;
          else {
            throw {
              isUserError: true,
              response,
              url: url.toString(),
              body: data,
            } as IRequestError;
          }
        })
        .catch((error: IRequestError | unknown) => {
          if ((error as IRequestError).isUserError) throw error;
          throw {isUserError: true, response, body: error} as IRequestError;
        });
    })
    .catch((error: IRequestError | unknown) => {
      if ((error as Error).name === 'TypeError') {
        const currentError = error as Error;
        antMessage.error(
          JSON.stringify({
            url,
            message: currentError.message,
            stack: currentError.stack,
          }),
          10,
        );
      }

      if ((error as IRequestError).isUserError) console.error(error);

      throw error;
    });
}

export default doRequest;
